// Duplicate booking detection for the Bookings Manager tab.
//
// Mirrors prm-bookings/util/dedupe-bookings.js (same flight + date + normalised
// name, part bookings and desk placeholders left alone, never discard handler
// work) but also flags looser matches for a human to review — nothing here
// writes, it only groups and ranks.

import { PRMCodes } from "./constants";

// Fields that mean a handler has already worked this booking. A booking
// carrying any of these must be the one kept in a merge.
export const OPERATIONAL_FIELDS = [
    "paxPositionInfo",
    "departureJourney",
    "arrivalJourney",
    "paxRejection",
];

// Desk placeholders are reused across different passengers on one flight.
const PLACEHOLDER_NAMES = ["test", "coord", "co ord", "co ord pax", "unknown passenger", "scan bp"];

const SOURCE_PRIORITY = ["PORTAL", "SITA", "APP", "WN", "NK", "CONNECT"];

// Blank fields on the kept booking that a merge fills from the removed one.
const MERGE_FILL_FIELDS = [
    "bio",
    "notes",
    "dueTime",
    "referenceNumber",
    "email",
    "phone",
    "phoneNumber",
    "language",
    "equipment",
    "equipmentInformation",
    "serviceAnimal",
    "battery",
];

export const MATCH_LEVELS = {
    LIKELY: "LIKELY",
    POSSIBLE: "POSSIBLE",
};

const isPopulated = (value) => {
    if (value === null || value === undefined || value === "") return false;
    if (Array.isArray(value)) return value.length > 0;
    if (typeof value === "object") return Object.keys(value).length > 0;
    return true;
};

export const operationalFields = (booking) => OPERATIONAL_FIELDS.filter((field) => isPopulated(booking[field]));

export const getSource = (booking) => {
    if (booking.isConnectBooking || booking._id?.startsWith("CONNECT")) return "CONNECT";
    if (booking.portal) return "PORTAL";
    if (booking.userId === "SITA") return "SITA";
    if (booking.userId === "PORTAL") return "PORTAL";
    if (booking.userId === "WN") return "WN";
    if (booking.userId === "NK") return "NK";
    return "APP";
};

// Staff splits from the timeline ("-part1") share name and flight with the
// original on purpose, so they are never flagged or merged.
export const isPartBooking = (booking) =>
    Boolean(
        booking._id?.includes("-part") ||
        booking.duplicatedFromId ||
        booking.bookingType === "DUPLICATE" ||
        (Array.isArray(booking.duplicateBookings) && booking.duplicateBookings.length > 0)
    );

const flattenName = (name) =>
    typeof name === "string" ? name.toLowerCase().replace(/[^a-z]+/g, " ").trim().replace(/\s+/g, " ") : "";

export const isPlaceholderName = (name) => {
    const flat = flattenName(name);
    if (!flat) return true;
    return PLACEHOLDER_NAMES.some((p) =>
        flat === p || flat.startsWith(`${p} `) || flat.endsWith(` ${p}`) || flat.includes(` ${p} `)
    );
};

// Lowercase letter-only tokens, titles dropped. SITA glues the title onto the
// first name ("GREER/NUALAMRS"), so a trailing title is stripped from longer
// tokens too — applied to every name, so both sides normalise the same way.
export const nameTokens = (name) => {
    if (!name || typeof name !== "string") return [];
    return name
        .replace(/[/,]/g, " ")
        .toLowerCase()
        .split(/\s+/)
        .map((token) => token.replace(/[^a-z]/g, ""))
        .filter((token) => token && !/^(mrs|miss|mstr|mr|ms|dr)$/.test(token))
        .map((token) => token.replace(/^(.{2,}?)(mrs|miss|mstr|mr|ms)$/, "$1"))
        .sort();
};

const flightKey = (booking) => {
    const f = booking.flightConfirmationDetails || {};
    const carrier = (f.carrierCode || booking.originalCarrierCode || "").toUpperCase();
    const flightNumber = String(f.flightNumber ?? "").replace(/^0+/, "");
    if (!flightNumber || !f.departureDate) return null;
    return [f.origin, f.destination, carrier, flightNumber, String(f.departureDate).slice(0, 10)].join("|");
};

// "j smith" ~ "john smith", "smith" ~ "john smith"; "mary smith" !~ "john smith".
// Needs one shared whole token of 3+ letters, and every token of the shorter
// name must match a different token of the longer one exactly or as a prefix.
export const similarNames = (a, b) => {
    const [short, long] = a.length <= b.length ? [a, b] : [b, a];
    if (!short.length) return false;
    if (!short.some((token) => token.length >= 3 && long.includes(token))) return false;

    const remaining = [...long];
    // exact matches first so a prefix match can't steal a token another needs
    const unmatched = short.filter((token) => {
        const i = remaining.indexOf(token);
        if (i === -1) return true;
        remaining.splice(i, 1);
        return false;
    });
    return unmatched.every((token) => {
        const i = remaining.findIndex((other) => other.startsWith(token) || token.startsWith(other));
        if (i === -1) return false;
        remaining.splice(i, 1);
        return true;
    });
};

// Keep-first order: handler work, then source priority, then oldest.
export const rankBookings = (bookings) => {
    const sourceRank = (booking) => {
        const i = SOURCE_PRIORITY.indexOf(getSource(booking));
        return i === -1 ? SOURCE_PRIORITY.length : i;
    };
    return [...bookings].sort((a, b) => {
        const opDiff = operationalFields(b).length - operationalFields(a).length;
        if (opDiff) return opDiff;
        const srcDiff = sourceRank(a) - sourceRank(b);
        if (srcDiff) return srcDiff;
        const created = (booking) => Date.parse(booking.dateCreated) || Number.MAX_SAFE_INTEGER;
        const createdDiff = created(a) - created(b);
        if (createdDiff) return createdDiff;
        return a._id < b._id ? -1 : 1;
    });
};

/**
 * Group the day's bookings so similar ones sit together.
 *
 * @param {Array<{booking: object, direction: "Departure"|"Arrival"}>} rows
 * @returns {Array<{id, level, reasons: string[], rows}>} one group per passenger;
 *          level is null for a booking with no match
 */
export const groupBookings = (rows) => {
    const parent = rows.map((_, i) => i);
    const find = (i) => (parent[i] === i ? i : (parent[i] = find(parent[i])));
    const edges = [];

    const link = (i, j, level, reason) => {
        edges.push({ i, j, level, reason });
        parent[find(i)] = find(j);
    };

    const candidates = rows
        .map((row, i) => ({
            i,
            row,
            tokens: nameTokens(row.booking.name),
            flight: flightKey(row.booking),
        }))
        .filter(({ row, tokens }) =>
            tokens.length && !isPartBooking(row.booking) && !isPlaceholderName(row.booking.name)
        );

    // same flight: exact or similar name
    const byFlight = new Map();
    candidates.forEach((c) => {
        if (!c.flight) return;
        if (!byFlight.has(c.flight)) byFlight.set(c.flight, []);
        byFlight.get(c.flight).push(c);
    });
    byFlight.forEach((list) => {
        for (let x = 0; x < list.length; x++) {
            for (let y = x + 1; y < list.length; y++) {
                const a = list[x];
                const b = list[y];
                if (a.tokens.join(" ") === b.tokens.join(" ")) {
                    link(a.i, b.i, MATCH_LEVELS.LIKELY, "Same name on the same flight");
                } else if (similarNames(a.tokens, b.tokens)) {
                    link(a.i, b.i, MATCH_LEVELS.POSSIBLE, "Similar name on the same flight");
                }
            }
        }
    });

    // same name, same direction, different flight — an arrival and a departure
    // with the same name is a normal transfer, so direction must match
    const byName = new Map();
    candidates.forEach((c) => {
        const key = `${c.row.direction}|${c.tokens.join(" ")}`;
        if (!byName.has(key)) byName.set(key, []);
        byName.get(key).push(c);
    });
    byName.forEach((list) => {
        for (let x = 0; x < list.length; x++) {
            for (let y = x + 1; y < list.length; y++) {
                if (list[x].flight !== list[y].flight) {
                    link(list[x].i, list[y].i, MATCH_LEVELS.POSSIBLE, "Same name on another flight today");
                }
            }
        }
    });

    const groups = new Map();
    rows.forEach((row, i) => {
        const root = find(i);
        if (!groups.has(root)) groups.set(root, { id: root, level: null, reasons: new Set(), rows: [] });
        groups.get(root).rows.push(row);
    });
    edges.forEach(({ i, level, reason }) => {
        const group = groups.get(find(i));
        if (group.level !== MATCH_LEVELS.LIKELY) group.level = level;
        group.reasons.add(reason);
    });

    return [...groups.values()].map((group) => {
        const ranked = rankBookings(group.rows.map((row) => row.booking));
        return {
            ...group,
            reasons: [...group.reasons],
            rows: ranked.map((booking) => group.rows.find((row) => row.booking === booking)),
        };
    });
};

// PRM codes found in a booking's assistance lines (SITA sends free text).
export const assistanceCodes = (booking) => {
    const lines = Array.isArray(booking.assistance) ? booking.assistance : [];
    return [...new Set(PRMCodes.filter((code) => lines.some((line) => line && line.includes(code))))];
};

/**
 * Why two bookings can't be merged, or null if they can. keep must be the one
 * carrying handler work when only one does.
 */
export const mergeBlocker = (keep, remove) => {
    if (keep._id === remove._id) return "Pick two different bookings.";
    if (isPartBooking(keep) || isPartBooking(remove)) {
        return "Part bookings are deliberate splits from the timeline and can't be merged.";
    }
    if (operationalFields(keep).length && operationalFields(remove).length) {
        return "Both bookings have handler activity (check-in, journey or rejection). Reconcile them on the timeline instead.";
    }
    if (operationalFields(remove).length) {
        return "The booking being removed has handler activity — keep that one instead.";
    }
    return null;
};

/**
 * Fields to patch onto the kept booking: blanks filled from the removed one,
 * assistance lines for PRM codes it lacks, and an audit entry.
 */
export const buildMergePatch = (keep, remove, { mergedById, mergedByName, now = new Date() } = {}) => {
    const patch = {};

    MERGE_FILL_FIELDS.forEach((field) => {
        if (!isPopulated(keep[field]) && isPopulated(remove[field])) patch[field] = remove[field];
    });

    ["originAssistance", "destinationAssistance"].forEach((field) => {
        if (!keep[field] && remove[field]) patch[field] = remove[field];
    });

    // only bring over lines that add a PRM code — SITA free text and portal
    // codes for the same need would otherwise both show
    const keptCodes = assistanceCodes(keep);
    const extraLines = (Array.isArray(remove.assistance) ? remove.assistance : []).filter((line) =>
        PRMCodes.some((code) => line && line.includes(code) && !keptCodes.includes(code))
    );
    if (extraLines.length) patch.assistance = [...(keep.assistance || []), ...extraLines];

    const iso = now.toISOString();
    patch.mergedBookings = [
        ...(keep.mergedBookings || []),
        {
            bookingId: remove._id,
            source: getSource(remove),
            mergedAt: iso,
            mergedById: mergedById ?? null,
            mergedByName: mergedByName ?? null,
        },
    ];
    patch.historicInfo = [...(keep.historicInfo || []), `${iso}_PORTAL_MERGED_${remove._id}`];

    return patch;
};
